import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import CampoBusca from '../componentes/CampoBusca';
import CartaoKPI from '../componentes/CartaoKPI';
import Ordenacao from '../componentes/Ordenacao';
import Segmentado from '../componentes/Segmentado';
import { listarFiados } from '../lib/consultas';
import { chavesDasPessoas, DIAS_PARA_ATRASO, estaAberta, estaAtrasada } from '../lib/fiado';
import { brl, hojeISO } from '../lib/formato';
import { casaBusca, porData, porNumero, porTexto } from '../lib/listas';
import { mensagemDeErro } from '../lib/supabase';
import { CartaoDoFiado, useRecarregarFiado } from './FiadoConta';
import FiadoPorPessoa from './FiadoPorPessoa';

// Quem está nos devendo (migração 042). Até 17/08/2026 a venda fiada lançava
// entrada no caixa como se tivesse sido paga — o erro de modelo mais sério do
// sistema. Agora ela é dívida, e o caixa só a conhece no dia do acerto.
//
// A lista abre pelo mais ANTIGO: no fiado, o que envelhece é o que se perde.
//
// A 065 trouxe os dois pedidos do dono de 27/08/2026: receber PEÇA A PEÇA
// (marcando quais linhas da venda o dinheiro cobriu) e o DESCONTO do acerto
// com oficina, que abate a dívida sem mexer na venda nem no caixa.
//
// DUAS VISÕES desde 26/09/2026, a pedido do dono: "por venda" (esta lista, a
// do dia a dia) e "por pessoa" (FiadoPorPessoa.tsx: uma linha por cliente,
// que abre com as dívidas dele, e os números da cobrança — prazo médio,
// idade da dívida, atraso). A conta e as ações moram em FiadoConta.tsx e são
// as mesmas nas duas.
//
// (O dataBR daqui era uma cópia local que fatiava o timestamptz em texto —
// pegava a data em UTC, errada entre 21h e meia-noite. O de lib/formato
// converte de verdade.)

type Visao = 'vendas' | 'pessoas';

const VISOES = [
  { id: 'vendas', rotulo: 'Por venda' },
  { id: 'pessoas', rotulo: 'Por pessoa' },
] as const satisfies ReadonlyArray<{ id: Visao; rotulo: string }>;

/** A visão de cada aparelho fica lembrada: o dono, que cobra, abre por pessoa;
 *  o balcão, que recebe venda a venda, continua abrindo por venda. */
const CHAVE_VISAO = 'fiado-visao';

function visaoLembrada(): Visao {
  try {
    return localStorage.getItem(CHAVE_VISAO) === 'pessoas' ? 'pessoas' : 'vendas';
  } catch {
    return 'vendas'; // navegador sem armazenamento: só não lembra
  }
}

export default function Fiado() {
  // A visão vai na URL (`/fiado?ver=pessoas`): recarregar a página, voltar e
  // mandar o link mantêm a mesma tela. Sem nada na URL, vale a lembrada.
  const [params, setParams] = useSearchParams();
  const pedida = params.get('ver');
  const visao: Visao = pedida === 'pessoas' || pedida === 'vendas' ? pedida : visaoLembrada();

  const mudarVisao = (v: Visao) => {
    try { localStorage.setItem(CHAVE_VISAO, v); } catch { /* sem armazenamento */ }
    setParams({ ver: v }, { replace: true });
  };

  return (
    <Layout titulo="Fiado — quem está devendo">
      <div className="space-y-4">
        <Segmentado itens={VISOES} valor={visao} aoMudar={mudarVisao} />
        {visao === 'pessoas' ? <FiadoPorPessoa /> : <FiadoPorVenda />}
      </div>
    </Layout>
  );
}

type OrdemFiado = 'antiga' | 'maior' | 'menor' | 'cliente' | 'recente' | 'vencimento';

const ORDENS_FIADO = [
  // A dívida mais VELHA em cima é o padrão porque é a ordem de cobrar: quanto
  // mais tempo passa, menos se recebe. Era a única ordem que existia aqui.
  { id: 'antiga', rotulo: 'Dívida mais antiga' },
  { id: 'maior', rotulo: 'Maior saldo' },
  { id: 'menor', rotulo: 'Menor saldo' },
  { id: 'vencimento', rotulo: 'Vence antes' },
  { id: 'cliente', rotulo: 'Cliente (A–Z)' },
  { id: 'recente', rotulo: 'Mais recente' },
] as const satisfies ReadonlyArray<{ id: OrdemFiado; rotulo: string }>;

/** Uma linha por VENDA fiada — a lista de sempre. */
function FiadoPorVenda() {
  const [incluirQuitados, setIncluirQuitados] = useState(false);
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<OrdemFiado>('antiga');
  // A cobrança de verdade começa aqui: o dono pediu para achar rápido "quem
  // está devendo há mais de um mês" sem ler a lista inteira.
  const [soVencidas, setSoVencidas] = useState(false);

  const fiados = useQuery({
    queryKey: ['fiados', incluirQuitados],
    queryFn: () => listarFiados(incluirQuitados),
  });
  const recarregar = useRecarregarFiado();

  const hoje = hojeISO();
  const lista = useMemo(() => {
    const filtradas = (fiados.data ?? []).filter((f) => (
      casaBusca(busca, f.cliente, f.vendedor, f.observacao)
      && (!soVencidas || (estaAtrasada(f, hoje) && estaAberta(f)))
    ));
    return [...filtradas].sort((a, b) => {
      if (ordem === 'maior') return porNumero(Number(a.saldo), Number(b.saldo), 'desc');
      if (ordem === 'menor') return porNumero(Number(a.saldo), Number(b.saldo), 'asc');
      if (ordem === 'cliente') return porTexto(a.cliente, b.cliente) || porData(a.vendida_em, b.vendida_em);
      if (ordem === 'recente') return porData(a.vendida_em, b.vendida_em, 'desc');
      // Quem TEM data combinada vem antes de quem não tem: o combinado é o que
      // dá direito de cobrar, e é por ele que se liga primeiro.
      if (ordem === 'vencimento') {
        return porData(a.vencimento, b.vencimento, 'asc') || porData(a.vendida_em, b.vendida_em, 'asc');
      }
      return porData(a.vendida_em, b.vendida_em, 'asc');
    });
  }, [fiados.data, busca, soVencidas, ordem, hoje]);

  // Os cartões seguem o filtro (mesma régua da tela de Vendas): buscar uma
  // oficina e ver quanto ELA deve é a pergunta de quem vai cobrar.
  const abertos = lista.filter(estaAberta);
  const totalDevido = abertos.reduce((s, f) => s + Number(f.saldo), 0);
  const maisVelho = abertos.reduce((m, f) => Math.max(m, f.dias_em_aberto), 0);
  // PESSOAS, não vendas: até 26/09/2026 este cartão dizia "Clientes devendo"
  // e contava VENDAS em aberto — 77 no dia, para 30 pessoas. A régua de quem
  // é quem é a mesma da visão por pessoa (chavesDasPessoas).
  const pessoas = new Set(chavesDasPessoas(abertos).values()).size;
  const filtrando = busca.trim() !== '' || soVencidas;

  return (
    <div className="space-y-4">
      {/* Duas colunas no celular pelo mesmo motivo da tela de contas: em
          375px o cartão de 1/3 corta qualquer valor com milhar. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
        <CartaoKPI rotulo="Total a receber" valor={brl(totalDevido)}
                   className="col-span-2 sm:col-span-1" />
        <CartaoKPI rotulo="Clientes devendo" valor={pessoas}
                   detalhe={`${abertos.length} ${abertos.length === 1 ? 'venda' : 'vendas'} em aberto`} />
        <CartaoKPI rotulo="Dívida mais antiga"
                   valor={`${maisVelho} ${maisVelho === 1 ? 'dia' : 'dias'}`}
                   tom={maisVelho > DIAS_PARA_ATRASO ? 'alerta' : 'neutro'} />
      </div>

      <div className="cartao space-y-3 p-3">
        <CampoBusca
          valor={busca}
          aoMudar={setBusca}
          placeholder="Buscar por cliente, vendedor ou observação"
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Ordenacao opcoes={ORDENS_FIADO} valor={ordem} aoMudar={setOrdem} />
          <p className="text-xs text-dim">
            {filtrando ? `${lista.length} de ${fiados.data?.length ?? 0}` : `${lista.length}`} conta(s)
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={soVencidas}
                 onChange={(e) => setSoVencidas(e.target.checked)} />
          Só as atrasadas (passou a data combinada, ou {DIAS_PARA_ATRASO} dias sem data)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={incluirQuitados}
                 onChange={(e) => setIncluirQuitados(e.target.checked)} />
          Mostrar também os já quitados e os dados como perdidos
        </label>
      </div>

      {fiados.isPending ? <Carregando /> : null}
      {fiados.error ? <Aviso tipo="erro">{mensagemDeErro(fiados.error)}</Aviso> : null}

      <ul className="space-y-2">
        {lista.map((f) => (
          <li key={f.id}>
            <CartaoDoFiado f={f} hoje={hoje} aoMudar={recarregar} />
          </li>
        ))}
        {lista.length === 0 && !fiados.isPending && (
          <li className="text-sm text-dim">
            {filtrando && (fiados.data?.length ?? 0) > 0
              ? 'Nenhuma conta casa com esses filtros.'
              : 'Ninguém devendo. 👏'}
          </li>
        )}
      </ul>
    </div>
  );
}
